import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { BattingRankingStats, DataSection, PitchingRankingStats, RankingScopeContext, ResultData, ScoringData, StatType } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import MultiRankingStatCalculator from "./../MultiRankingStatCalculator.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import ResultsSlice from "../../../valueObjects/ResultsSlice.js";
import resultsSliceForPitchType from "../../../classificators/resultsSlice/resultsSliceForPitchType.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import battingScopeContext from "../../../classificators/resultsSlice/battingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import ClassifiedPitchesFilter from "../../../filters/contextual/ClassifiedPitchesFilter.js";
import ContextualFilterBase from "../../../filters/contextual/ContextualFilterBase.js";
import * as PlateAppearanceResultClassifiers from '../../../classificators/plateAppearanceResult/index.js';
import sessionWithNewScoring from "../../../classificators/session/sessionWithNewScoring.js";
import PlateAppearanceResultToBaseNumberConverter from "../../../converters/PlateAppearanceResultToBaseNumberConverter.js";

class NewBaseHitsCalculator extends MultiRankingStatCalculator{
    constructor(
        context, customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache, contextualResultFilter
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(contextualResultFilter) !== 'undefined' && !(contextualResultFilter instanceof ContextualFilterBase)) {
            throw new TypeError('contextualResultFilter must be an instance inheriting from ContextualFilterBase class');
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }

        super(
            context === RankingScopeContext.Pitching.key
                ? [
                    PitchingRankingStats.HitsAllowed, PitchingRankingStats.HomeRunsAllowed,
                    PitchingRankingStats.BaseHitsAllowed, PitchingRankingStats.TwoBaseHitsAllowed, PitchingRankingStats.ThreeBaseHitsAllowed
                ]
                : [
                    BattingRankingStats.Hits, BattingRankingStats.HomeRuns,
                    BattingRankingStats.BaseHits, BattingRankingStats.TwoBaseHits, BattingRankingStats.ThreeBaseHits,
                    BattingRankingStats.TotalBases
                ]
            ,
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithNewScoring,
                satisfiesAll(
                    context === RankingScopeContext.Pitching.key 
                        ? pitchingScopeContext
                        : battingScopeContext
                    ,
                    resultsSliceForPitchType
                )
            ),
            skipCache
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.context = context;
        this.contextualFilter = contextualResultFilter || new ClassifiedPitchesFilter(this.resultsProcessor);
        this.hitClassifier = PlateAppearanceResultClassifiers.classifiesAsHit;
        this.homeRunClassifier = PlateAppearanceResultClassifiers.classifiesAsHomeRun;
        this.singleClassifier = PlateAppearanceResultClassifiers.classifiesAsSingle;
        this.doubleClassifier = PlateAppearanceResultClassifiers.classifiesAsDouble;
        this.tripleClassifier = PlateAppearanceResultClassifiers.classifiesAsTriple;
        this.plateAppearanceResultToBaseNrConverter = PlateAppearanceResultToBaseNumberConverter;
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        const pitches = this.filterResults(slice.getSmallestSlice(), session, league);

        const stats = this.resultsProcessor.calcStats(
            [StatType.Ratio.key],
            pitches,
            // TODO - simplify it...
            [ [ [] ].concat(
                ResultData.getNewDataPath(ScoringData.PlateAppearanceResult.key, DataSection.Scoring.key)
            ) ],
            false
        );

        const output = {};
        let hits = 0;
        let totalBases = 0;
        let homeRuns = 0;
        let singles = 0;
        let doubles = 0;
        let triples = 0;

        if (!stats || !stats.hasOwnProperty(ScoringData.PlateAppearanceResult.key) || !stats[ScoringData.PlateAppearanceResult.key][StatType.Ratio.key]){
            return output;
        }

        for (const result in stats[ScoringData.PlateAppearanceResult.key][StatType.Ratio.key]) {
            if (!Object.hasOwnProperty.call(stats[ScoringData.PlateAppearanceResult.key][StatType.Ratio.key], result)) {
                continue;
            }

            const count = stats[ScoringData.PlateAppearanceResult.key][StatType.Ratio.key][result].count;
            //hits and home runs are only common here
            if (this.hitClassifier(result)) {
                hits += count;
            }
            if (this.homeRunClassifier(result)) {
                homeRuns += count;
            }

            // todo, maybe else if ?
            if (this.singleClassifier(result)) {
                singles += count;
            }
            if (this.doubleClassifier(result)) {
                doubles += count;
            }
            if (this.tripleClassifier(result)) {
                triples += count;
            }

            if (this.context === RankingScopeContext.Pitching.key) {
                continue;
            }

            totalBases += this.plateAppearanceResultToBaseNrConverter(result) * count;
        }

        // now include hits and total bases
        if (hits > 0) {
            const baseHits = this.supportedParams[0];
            output[baseHits.key] = RankingStat.fromParameter(
                baseHits, this.numberFormatter.format(hits), hits
            );
        }
        if (homeRuns > 0) {
            const homeRunsHits = this.supportedParams[1];
            output[homeRunsHits.key] = RankingStat.fromParameter(
                homeRunsHits, this.numberFormatter.format(homeRuns), homeRuns
            );
        }
        if (singles > 0) {
            const oneBaseHits = this.supportedParams[2];
            output[oneBaseHits.key] = RankingStat.fromParameter(
                oneBaseHits, this.numberFormatter.format(singles), singles
            );
        }
        if (doubles > 0) {
            const twoBaseHits = this.supportedParams[3];
            output[twoBaseHits.key] = RankingStat.fromParameter(
                twoBaseHits, this.numberFormatter.format(doubles), doubles
            );
        }
        if (triples > 0) {
            const threeBaseHits = this.supportedParams[4];
            output[threeBaseHits.key] = RankingStat.fromParameter(
                threeBaseHits, this.numberFormatter.format(triples), triples
            );
        }
        if (totalBases > 0) {
            const sumBases = this.supportedParams[5];
            output[sumBases.key] = RankingStat.fromParameter(
                sumBases, this.numberFormatter.format(totalBases), totalBases
            );
        }

        return output;
    }

    filterResults(results, session, league) {
        return this.contextualFilter.filter(results, session, league);
    }
}

export default NewBaseHitsCalculator;